import React, { useMemo } from "react";
import {
    ArrowRightOutlined,
    CheckCircleOutlined,
    FileDoneOutlined,
} from "@ant-design/icons";
import {
    Card,
    Col,
    Descriptions,
    Empty,
    List,
    Row,
    Space,
    Table,
    Tabs,
    Tag,
    Timeline,
} from "antd";
import { ColumnsType } from "antd/lib/table";
import { WbAction, WbDocument, WbTask } from "../../workbench/types/WbTypes";
import {
    formatDateTime,
    formatFileSize,
    noop,
} from "../utils/workbenchNewSubjectView";

type Props = {
    broker: string;
    documents: WbDocument[];
    identifier: string;
    tasks: WbTask[];
    title: string;
    timelineActions: WbAction[];
};

const WorkbenchNewSubjectTabs: React.FC<Props> = ({
    broker,
    documents,
    identifier,
    tasks,
    timelineActions,
    title,
}) => {
    const documentColumns: ColumnsType<WbDocument> = [
        {
            title: "Document",
            dataIndex: "fileName",
            key: "fileName",
            render: (fileName: string) => (
                <a href="/" onClick={noop}>
                    {fileName}
                </a>
            ),
        },
        {
            title: "Task",
            dataIndex: "taskTitle",
            key: "taskTitle",
        },
        {
            title: "Uploaded",
            dataIndex: "uploadTime",
            key: "uploadTime",
            render: formatDateTime,
        },
        {
            title: "Size",
            dataIndex: "fileSize",
            key: "fileSize",
            render: formatFileSize,
        },
    ];

    const taskColumns: ColumnsType<WbTask> = [
        {
            title: "Task",
            dataIndex: "title",
            key: "title",
        },
        {
            title: "Assignee",
            dataIndex: "assignee",
            key: "assignee",
            render: (assignee?: string) => assignee || "-",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status?: string, task?: WbTask) => (
                <Tag color={task?.isDone ? "green" : "blue"}>
                    {task?.isDone ? "Done" : status || "Open"}
                </Tag>
            ),
        },
    ];

    const timelineItems = timelineActions.map((action) => ({
        key: action.actionEventId,
        color: action.status === "Completed" ? "green" : "blue",
        children: (
            <div className="workbench-new-subject-activity">
                <strong>{action.actionName || "Activity"}</strong>
                <span>{action.taskTitle}</span>
                <small>{formatDateTime(action.triggeredOn)}</small>
            </div>
        ),
    }));

    const items = useMemo(
        () => [
            {
                key: "program",
                label: "Program",
                children: (
                    <Row
                        align="stretch"
                        className="workbench-new-programme-row-layout"
                        gutter={[18, 18]}
                    >
                        <Col
                            xs={24}
                            lg={14}
                            className="workbench-new-programme-column"
                        >
                            <Card className="workbench-new-subject-card">
                                <div className="workbench-new-subject-card-header-row">
                                    <div>
                                        <div className="workbench-new-subject-card-title">
                                            Program structure
                                        </div>
                                        <p className="workbench-new-programme-copy">
                                            Declared excess of loss programme
                                            and extracted layer economics.
                                        </p>
                                    </div>
                                    <Tag className="workbench-new-programme-pill">
                                        2 layers
                                    </Tag>
                                </div>
                                <div className="workbench-new-programme-tree">
                                    <div className="workbench-new-programme-root">
                                        <span className="workbench-new-programme-root-name">
                                            <span className="workbench-new-programme-root-icon" />
                                            <strong>{title}</strong>
                                        </span>
                                        <span />
                                        <span />
                                    </div>
                                    <div className="workbench-new-programme-head">
                                        <span />
                                        <span>Limit</span>
                                        <span>Premium</span>
                                    </div>
                                    <div className="workbench-new-programme-row">
                                        <div className="workbench-new-programme-layer-name">
                                            <span className="workbench-new-programme-tree-line" />
                                            <span className="workbench-new-programme-layer-icon" />
                                            <strong>Layer 1</strong>
                                        </div>
                                        <span>USD 75,000,000</span>
                                        <span>USD 750,000</span>
                                    </div>
                                    <div className="workbench-new-programme-row">
                                        <div className="workbench-new-programme-layer-name">
                                            <span className="workbench-new-programme-tree-line" />
                                            <span className="workbench-new-programme-layer-icon" />
                                            <strong>Layer 2</strong>
                                        </div>
                                        <span>USD 50,000,000</span>
                                        <span>USD 500,000</span>
                                    </div>
                                </div>
                                <a
                                    className="workbench-new-programme-link"
                                    href="/"
                                    onClick={noop}
                                >
                                    View full structure <ArrowRightOutlined />
                                </a>
                            </Card>
                        </Col>
                        <Col
                            xs={24}
                            lg={10}
                            className="workbench-new-programme-column"
                        >
                            <Card className="workbench-new-subject-card workbench-new-subject-detail-card">
                                <div className="workbench-new-subject-card-title workbench-new-subject-card-title-small">
                                    Key terms extracted
                                </div>
                                <dl className="workbench-new-subject-terms">
                                    <div>
                                        <dt>Coverage</dt>
                                        <dd>Property Damage</dd>
                                    </div>
                                    <div>
                                        <dt>Territory</dt>
                                        <dd>Worldwide</dd>
                                    </div>
                                    <div>
                                        <dt>Deductible</dt>
                                        <dd>USD 250,000</dd>
                                    </div>
                                    <div>
                                        <dt>Broker</dt>
                                        <dd>{broker}</dd>
                                    </div>
                                    <div>
                                        <dt>Contract certainty</dt>
                                        <dd>
                                            <Tag className="workbench-new-programme-pill">
                                                Good
                                            </Tag>
                                        </dd>
                                    </div>
                                    <div>
                                        <dt>Remarks</dt>
                                        <dd>-</dd>
                                    </div>
                                </dl>
                                <a
                                    className="workbench-new-programme-link"
                                    href="/"
                                    onClick={noop}
                                >
                                    View all extracted data{" "}
                                    <ArrowRightOutlined />
                                </a>
                            </Card>
                        </Col>
                    </Row>
                ),
            },
            {
                key: "documents",
                label: `Documents ${documents.length}`,
                children: (
                    <Table
                        columns={documentColumns}
                        dataSource={documents}
                        pagination={false}
                        rowKey="packageId"
                        size="middle"
                    />
                ),
            },
            {
                key: "tasks",
                label: `Tasks ${tasks.length}`,
                children: (
                    <Table
                        columns={taskColumns}
                        dataSource={tasks}
                        pagination={false}
                        rowKey="id"
                        size="middle"
                    />
                ),
            },
            {
                key: "analyses",
                label: "AI Analyses 1",
                children: (
                    <Card className="workbench-new-subject-card">
                        <List
                            dataSource={[
                                "2 layers identified across this Excess of Loss program.",
                                "Worldwide coverage excluding US & Canada.",
                                "Layer 2 premium confirmation is still outstanding.",
                            ]}
                            renderItem={(item) => (
                                <List.Item>
                                    <FileDoneOutlined />
                                    <span>{item}</span>
                                </List.Item>
                            )}
                        />
                    </Card>
                ),
            },
            {
                key: "sics",
                label: (
                    <Space size={4}>
                        SICS
                        <CheckCircleOutlined className="workbench-new-subject-green" />
                    </Space>
                ),
                children: (
                    <Card className="workbench-new-subject-card">
                        <Descriptions column={1} size="small">
                            <Descriptions.Item label="Submission ID">
                                {identifier}
                            </Descriptions.Item>
                            <Descriptions.Item label="Current sync">
                                <Tag color="green">Matched</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Last reviewed">
                                Today, 10:24
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                ),
            },
            {
                key: "notes",
                label: "Notes",
                children: (
                    <Card className="workbench-new-subject-card">
                        <Empty description="No notes captured for this readonly view" />
                    </Card>
                ),
            },
            {
                key: "activity",
                label: "Activity",
                children: <Timeline items={timelineItems} />,
            },
        ],
        [broker, documents, identifier, tasks, timelineItems, title],
    );

    return <Tabs className="workbench-new-subject-tabs" items={items} />;
};

export default WorkbenchNewSubjectTabs;
